import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import {
  AiAgentsNamespaceGetKnowledgeBaseArgs,
  AiAgentsNamespaceUpdateKnowledgeBaseArgs,
  Mutation,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { LggOptionsDropdownButtonWithCustomTriggerControlled } from 'src/components/general/button/dropdown-button';
import { TableLayoutPlaceholder } from 'src/components/general/feedback/loading-placeholders';
import { MoreOptionsIcon } from 'src/components/general/table-helpers';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { PageContainer } from 'src/components/layout/page-containers';
import { TableLayoutPageContainer } from 'src/components/layout/table-layout-page-container';
import { getDeleteRowOption } from 'src/components/pages/ai-agents/components/ai-agents-table-columns';
import { KnowledgeTabs } from 'src/components/pages/ai-agents/components/knowledge-tabs';
import { DocumentSources } from 'src/components/pages/ai-agents/knowledge-base-sources/documents/document-sources';
import { WebsiteSources } from 'src/components/pages/ai-agents/knowledge-base-sources/websites/website-sources';
import { EditableTitle } from 'src/components/pages/ai-agents/knowledge-bases/components/editable-title';
import { useDeleteKnowledgeBase } from 'src/components/pages/ai-agents/knowledge-bases/hooks/use-delete-knowledge-base';
import {
  KNOWLEDGE_BASE_DETAIL_QUERY,
  UPDATE_KNOWLEDGE_BASE_MUTATION,
} from 'src/components/pages/ai-agents/knowledge-bases/queries';
import { NotFoundPage } from 'src/components/pages/errors/not-found-page';
import { useAddBreadcrumb } from 'src/hooks/use-add-breadcrumb';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useQueryParams } from 'src/hooks/use-query-params';

const DetailsPageContainer = styled(PageContainer)`
  ${up('md')} {
    padding: 30px;
  }
`;

const Card = styled(FlexColumn)`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 6px;

  ${up('md')} {
    min-height: calc(100vh - 120px);
  }
`;

const CardHeader = styled(FlexRow)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
  gap: 10px;
  height: 60px;
  justify-content: space-between;
  padding: 0 20px 0 15px;
`;

type KnowledgeBaseDetailsPageProps = {
  sectionUrl: string;
};

export const KnowledgeBaseDetailsPage = memo<KnowledgeBaseDetailsPageProps>(
  ({ sectionUrl }) => {
    const { t } = useTranslation(['aiAgents', 'common']);
    const history = useHistory();
    const { knowledgeBaseId } = useParams<{ knowledgeBaseId: string }>();
    const { pathname } = useLocation();
    const renameRequested = useQueryParams().rename === 'true';
    const { id: companyId } = useCurrentInstitution();
    const { hasPermission } = useAuthorization();
    const handleGraphQLError = useHandleGraphQLError();
    const canUpdate = hasPermission('ai:company.ai_agent_knowledge_base:update:all');
    const canDelete = hasPermission('ai:company.ai_agent_knowledge_base:delete:all');
    const [renaming, setRenaming] = useState(renameRequested);

    useEffect(() => {
      if (renameRequested) {
        history.replace(pathname);
      }
    }, [history, pathname, renameRequested]);

    const openDelete = useDeleteKnowledgeBase({
      onDeleted: () => history.push(`${sectionUrl}/knowledge-bases`),
    });

    const { data, loading } = useQuery<
      Pick<Query, 'aiAgents'>,
      AiAgentsNamespaceGetKnowledgeBaseArgs
    >(KNOWLEDGE_BASE_DETAIL_QUERY, {
      variables: { input: { companyId, id: knowledgeBaseId } },
      onError: (error) => handleGraphQLError(error, { isNavigation: true }),
    });
    useAddBreadcrumb(data?.aiAgents.getKnowledgeBase?.title ?? '');

    const [updateKnowledgeBase] = useMutation<
      Pick<Mutation, 'aiAgents'>,
      AiAgentsNamespaceUpdateKnowledgeBaseArgs
    >(UPDATE_KNOWLEDGE_BASE_MUTATION, { onError: handleGraphQLError });

    if (!data || loading) {
      return (
        <TableLayoutPageContainer>
          <TableLayoutPlaceholder />
        </TableLayoutPageContainer>
      );
    }

    const knowledgeBase = data.aiAgents.getKnowledgeBase;

    if (!knowledgeBase) {
      return <NotFoundPage />;
    }

    const rename = (title: string) => {
      setRenaming(false);
      void updateKnowledgeBase({
        variables: { input: { companyId, id: knowledgeBase.id, title } },
      });
    };

    return (
      <DetailsPageContainer data-lgg-id="knowledge-base-details-page">
        <Card>
          <CardHeader>
            <EditableTitle
              key={`${knowledgeBase.title}-${renaming}`}
              value={knowledgeBase.title}
              editable={canUpdate}
              startEditing={renaming}
              onSave={rename}
              onCancel={() => setRenaming(false)}
              testId="knowledge-base-title"
            />
            {(canUpdate || canDelete) && (
              <LggOptionsDropdownButtonWithCustomTriggerControlled
                customTrigger={
                  <MoreOptionsIcon
                    type="moreOptions"
                    lggTestId="knowledge-base-more-options"
                  />
                }
                customDropdownProps={{ getPopupContainer: (trigger) => trigger }}
                options={[
                  ...(canUpdate
                    ? [
                        {
                          label: t('aiAgents:pages.knowledgeBases.options.rename'),
                          icon: 'edit',
                          'data-lgg-id': 'knowledge-base-option-rename',
                          onClick: () => setRenaming(true),
                        },
                      ]
                    : []),
                  ...(canDelete
                    ? [
                        getDeleteRowOption({
                          label: t('common:delete'),
                          testId: 'knowledge-base-option-delete',
                          onClick: () => openDelete(knowledgeBase),
                        }),
                      ]
                    : []),
                ]}
              />
            )}
          </CardHeader>
          <KnowledgeTabs
            testId="knowledge-base-tabs"
            tabHeight={38}
            sourceContent={{
              websites: <WebsiteSources knowledgeBaseId={knowledgeBase.id} />,
              documents: <DocumentSources knowledgeBaseId={knowledgeBase.id} />,
            }}
          />
        </Card>
      </DetailsPageContainer>
    );
  },
);
